import {
  IReceivedDirectMessage,
  IRequiresAttention,
  ISelectorOption,
  IToDo,
} from 'providence-types';
import { StoredRequest } from '@core/utils/store/stored-request-decorator';
import {
  AccountingNotificationData,
  DashboardItem,
  DashboardUpdatableItem,
  MultiSetCompletedDirectMessage,
  MultiUpdateAssignees,
  SetCompletedDirectMessage,
} from '@models/interfaces';
import { DashboardActions } from '@store/actions';
import { CommonHttpService } from './common-http.service';

export class DashboardHttpService extends CommonHttpService {
  // users
  @StoredRequest(DashboardActions.getAdminList)
  getAdminList(): Promise<ISelectorOption[]> {
    return this.http.callApi.get(`${this.apiUrl}/dashboard/admin-list`);
  }

  @StoredRequest(DashboardActions.getUserList)
  getUserList(): Promise<ISelectorOption[]> {
    return this.http.callApi.get(`${this.apiUrl}/dashboard/user-list`);
  }

  // Accounting
  @StoredRequest(DashboardActions.getAccountingItems)
  getAccountingItems(): Promise<DashboardItem<AccountingNotificationData>[]> {
    return this.http.callApi.get(`${this.apiUrl}/dashboard/accounting`);
  }

  // ToDo
  @StoredRequest(DashboardActions.getAssignedToDoItems)
  getAssignedToDoItems(): Promise<DashboardItem<IToDo>[]> {
    return this.http.callApi.get(
      `${this.apiUrl}/dashboard/to-dos/assigned-to-me`,
    );
  }

  @StoredRequest(DashboardActions.getAllToDoItems)
  getAllToDoItems(): Promise<DashboardItem<IToDo>[]> {
    return this.http.callApi.get(`${this.apiUrl}/dashboard/to-dos`);
  }

  @StoredRequest(DashboardActions.updateToDoItem)
  async updateToDoItem({
    reporter,
    ...body
  }: DashboardUpdatableItem): Promise<DashboardUpdatableItem> {
    await this.http.callApi.patch(`${this.apiUrl}/dashboard/to-dos`, body);

    return {
      reporter,
      ...body,
    };
  }

  @StoredRequest(DashboardActions.multiUpdateToDoAssignees)
  async multiUpdateToDoAssignees({
    reporter,
    ...body
  }: MultiUpdateAssignees): Promise<MultiUpdateAssignees> {
    await this.http.callApi.patch(
      `${this.apiUrl}/dashboard/to-dos/multi-assign`,
      body,
    );

    return {
      reporter,
      ...body,
    };
  }

  // Requires Attention
  @StoredRequest(DashboardActions.getAssignedRequiresAttentionItems)
  getAssignedRequiresAttentionItems(): Promise<
    DashboardItem<IRequiresAttention>[]
  > {
    return this.http.callApi.get(
      `${this.apiUrl}/dashboard/requires-attentions/assigned-to-me`,
    );
  }

  @StoredRequest(DashboardActions.getAllRequiresAttentionItems)
  getAllRequiresAttentionItems(): Promise<DashboardItem<IRequiresAttention>[]> {
    return this.http.callApi.get(
      `${this.apiUrl}/dashboard/requires-attentions`,
    );
  }

  @StoredRequest(DashboardActions.updateRequiresAttentionItem)
  async updateRequiresAttentionItem({
    reporter,
    ...body
  }: DashboardUpdatableItem): Promise<DashboardUpdatableItem> {
    await this.http.callApi.patch(
      `${this.apiUrl}/dashboard/requires-attentions`,
      body,
    );

    return { reporter, ...body };
  }

  @StoredRequest(DashboardActions.multiUpdateRequiresAttentionAssignees)
  async multiUpdateRequiresAttentionAssignees({
    reporter,
    ...body
  }: MultiUpdateAssignees): Promise<MultiUpdateAssignees> {
    await this.http.callApi.patch(
      `${this.apiUrl}/dashboard/requires-attentions/multi-assign`,
      body,
    );

    return { reporter, ...body };
  }

  // Direct Messages
  @StoredRequest(DashboardActions.getDirectMessage)
  getDirectMessages(
    completed: boolean,
  ): Promise<DashboardItem<IReceivedDirectMessage>[]> {
    return this.http.callApi.get(
      `${this.apiUrl}/dashboard/direct-messages`,
      null,
      { params: { completed } },
    );
  }

  @StoredRequest(DashboardActions.setCompletedDirectMessage)
  async setCompletedDirectMessage(
    _id: string,
    completed: boolean,
  ): Promise<SetCompletedDirectMessage> {
    await this.http.callApi.patch(
      `${this.apiUrl}/dashboard/direct-messages/completed`,
      null,
      { params: { _id } },
    );

    return { _id, completed };
  }

  @StoredRequest(DashboardActions.multiSetCompletedDirectMessage)
  async multiSetCompletedDirectMessage(
    recordIds: string[],
    completed: boolean,
  ): Promise<MultiSetCompletedDirectMessage> {
    await this.http.callApi.patch(
      `${this.apiUrl}/dashboard/direct-messages/multi-completed`,
      null,
      { params: { recordIds } },
    );

    return { recordIds, completed };
  }
}
